import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { todayBR } from "../lib/dates";
import { Coins, TrendingUp, Trash2, UserCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

function formatBRL(n) {
  const sign = n < 0 ? "-" : n > 0 ? "+" : "";
  return `${sign}R$ ${Math.abs(n).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y.slice(2)}`;
}

// Compute inclusive date range [start, end] as ISO YYYY-MM-DD strings for a period.
function computeRange(periodType, periodDate, periodMonth, periodYear) {
  if (periodType === "day") return [periodDate, periodDate];
  if (periodType === "week") {
    // Use Brasília timezone to determine current week (Mon-Sun)
    const todayStr = todayBR();
    const [ty, tm, td] = todayStr.split("-").map(Number);
    const t = new Date(Date.UTC(ty, tm - 1, td));
    const day = t.getUTCDay() || 7;
    const monday = new Date(t);
    monday.setUTCDate(t.getUTCDate() - (day - 1));
    const sunday = new Date(monday);
    sunday.setUTCDate(monday.getUTCDate() + 6);
    return [monday.toISOString().slice(0, 10), sunday.toISOString().slice(0, 10)];
  }
  if (periodType === "month") return [`${periodMonth}-01`, `${periodMonth}-31`];
  if (periodType === "year") return [`${periodYear}-01-01`, `${periodYear}-12-31`];
  return [null, null];
}

export default function GirosPanel() {
  const [entries, setEntries] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmId, setConfirmId] = useState(null);

  // Filters
  const [periodType, setPeriodType] = useState("month");
  const [periodDate, setPeriodDate] = useState(() => todayBR());
  const [periodMonth, setPeriodMonth] = useState(() => todayBR().slice(0, 7));
  const [periodYear, setPeriodYear] = useState(() => todayBR().slice(0, 4));
  const [casaFilter, setCasaFilter] = useState("all");
  const [holderFilter, setHolderFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const [entriesRes, banksRes] = await Promise.all([
        api.get("/entries", { params: { operation_type: "giros" } }),
        api.get("/banks"),
      ]);
      setEntries(entriesRes.data);
      setBanks(banksRes.data.banks || []);
    } catch {
      toast.error("Erro ao carregar giros");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/entries/${id}`);
      toast.success("Registro excluído");
      load();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  // Distinct casas (from entries)
  const casaOptions = useMemo(() => {
    const set = new Set();
    for (const e of entries) {
      const n = e.bookmakers?.[0]?.name;
      if (n) set.add(n);
    }
    return Array.from(set).sort();
  }, [entries]);

  // Holder → set of bank names
  const holderToNames = useMemo(() => {
    const m = {};
    for (const b of banks) {
      const key = (b.account_holder || "").trim();
      if (!key) continue;
      if (!m[key]) m[key] = new Set();
      m[key].add(b.name);
    }
    return m;
  }, [banks]);
  const holderOptions = useMemo(() => Object.keys(holderToNames).sort(), [holderToNames]);

  // Filtered entries
  const filtered = useMemo(() => {
    const [start, end] = computeRange(periodType, periodDate, periodMonth, periodYear);
    return entries.filter((e) => {
      if (start && (e.date < start || e.date > end)) return false;
      const casa = e.bookmakers?.[0]?.name || "";
      if (casaFilter !== "all" && casa !== casaFilter) return false;
      if (holderFilter !== "all") {
        const names = holderToNames[holderFilter];
        if (!names || !names.has(casa)) return false;
      }
      return true;
    });
  }, [entries, periodType, periodDate, periodMonth, periodYear, casaFilter, holderFilter, holderToNames]);

  const totalWon = filtered.reduce((s, e) => s + (e.profit || 0), 0);
  const allTimeWon = entries.reduce((s, e) => s + (e.profit || 0), 0);
  const periodLabel =
    periodType === "month" && periodMonth === todayBR().slice(0, 7)
      ? "Mês atual"
      : periodType === "month"
      ? `Mês ${periodMonth.split("-").reverse().join("/")}`
      : periodType === "day"
      ? "Dia"
      : periodType === "week"
      ? "Semana atual"
      : periodType === "year"
      ? `Ano ${periodYear}`
      : "Todo o período";

  // Available months/years from data (for the pickers)
  const availableMonths = useMemo(
    () => Array.from(new Set(entries.map((e) => (e.date || "").slice(0, 7)).filter(Boolean))).sort().reverse(),
    [entries]
  );
  const availableYears = useMemo(
    () => Array.from(new Set(entries.map((e) => (e.date || "").slice(0, 4)).filter(Boolean))).sort().reverse(),
    [entries]
  );

  // Aggregate per casa (from filtered)
  const byCasa = {};
  for (const e of filtered) {
    const casa = e.bookmakers?.[0]?.name || "—";
    if (!byCasa[casa]) byCasa[casa] = { total: 0, count: 0 };
    byCasa[casa].total += e.profit || 0;
    byCasa[casa].count += 1;
  }
  const casaList = Object.entries(byCasa)
    .map(([casa, v]) => ({ casa, ...v }))
    .sort((a, b) => b.total - a.total);

  if (loading) {
    return <div className="h-40 animate-pulse rounded-xl border border-white/10 bg-[#121212]" />;
  }

  return (
    <div className="space-y-6">
      {/* Filter card */}
      <div
        data-testid="giros-filter-hero"
        className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#141414] to-[#0F0F0F] p-6 md:p-8"
      >
        <div className="mb-6 flex flex-wrap items-center gap-2">
          {[
            { v: "all", label: "Todos" },
            { v: "day", label: "Diário" },
            { v: "week", label: "Semanal" },
            { v: "month", label: "Mensal" },
            { v: "year", label: "Anual" },
          ].map((p) => {
            const active = periodType === p.v;
            return (
              <button
                key={p.v}
                data-testid={`giros-period-${p.v}`}
                onClick={() => setPeriodType(p.v)}
                className="h-9 rounded-full border px-4 text-xs font-semibold"
                style={{
                  borderColor: active ? "#F472B6" : "rgba(255,255,255,0.1)",
                  backgroundColor: active ? "#F472B6" : "transparent",
                  color: active ? "#000" : "#D4D4D8",
                  transition: "background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease",
                }}
              >
                {p.label}
              </button>
            );
          })}
          {periodType === "day" && (
            <Input
              data-testid="giros-period-day"
              type="date"
              value={periodDate}
              onChange={(e) => setPeriodDate(e.target.value)}
              className="h-9 w-[160px] bg-black/40 font-mono-num text-sm"
            />
          )}
          {periodType === "month" && (
            <Select value={periodMonth} onValueChange={setPeriodMonth}>
              <SelectTrigger data-testid="giros-period-month" className="h-9 w-[160px] border-white/10 bg-black/40 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-white/10 bg-[#121212] text-zinc-200">
                {(availableMonths.length ? availableMonths : [new Date().toISOString().slice(0, 7)]).map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {periodType === "year" && (
            <Select value={periodYear} onValueChange={setPeriodYear}>
              <SelectTrigger data-testid="giros-period-year" className="h-9 w-[120px] border-white/10 bg-black/40 font-mono-num text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-white/10 bg-[#121212] text-zinc-200">
                {(availableYears.length ? availableYears : [String(new Date().getFullYear())]).map((y) => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <span className="mx-2 h-6 w-px bg-white/10" />
          <Select value={casaFilter} onValueChange={setCasaFilter}>
            <SelectTrigger data-testid="giros-casa-filter" className="h-9 w-[170px] border-white/10 bg-black/40 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-white/10 bg-[#121212] text-zinc-200">
              <SelectItem value="all">Todas casas</SelectItem>
              {casaOptions.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2 rounded-full border border-white/10 bg-black/40 pl-3 pr-1">
            <UserCircle2 className="h-3.5 w-3.5 text-zinc-500" />
            <Select value={holderFilter} onValueChange={setHolderFilter}>
              <SelectTrigger data-testid="giros-holder-filter" className="h-9 w-[180px] border-0 bg-transparent text-sm shadow-none focus:ring-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-white/10 bg-[#121212] text-zinc-200">
                <SelectItem value="all">Todos titulares</SelectItem>
                {holderOptions.map((h) => (
                  <SelectItem key={h} value={h}>{h}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="text-[0.75rem] uppercase tracking-widest text-zinc-500">
          Lucro em giros · <span className="text-[#F472B6]">{periodLabel}</span>
          {casaFilter !== "all" && <span className="ml-1 text-[#F472B6]">· {casaFilter}</span>}
          {holderFilter !== "all" && <span className="ml-1 text-[#F472B6]">· {holderFilter}</span>}
        </div>
        <div
          data-testid="giros-total"
          className="mt-2 font-mono-num text-6xl font-black tracking-tight md:text-7xl"
          style={{ color: totalWon >= 0 ? "#F472B6" : "#FF3B30" }}
        >
          {formatBRL(totalWon)}
        </div>
        <div className="mt-1 text-xs text-zinc-500">
          {filtered.length} giro{filtered.length === 1 ? "" : "s"} no período
          {periodType !== "all" && (
            <span data-testid="giros-alltime" className="ml-2 text-zinc-600">
              · Total geral {formatBRL(allTimeWon)}
            </span>
          )}
        </div>
      </div>

      {/* Per-casa breakdown */}
      {casaList.length > 0 && (
        <div className="rounded-xl border border-white/10 bg-[#121212] p-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="font-display text-lg font-bold text-white">Por casa</h3>
              <p className="text-xs text-zinc-500">Ganhos agrupados por casa no período filtrado</p>
            </div>
            <TrendingUp className="h-4 w-4 text-zinc-500" />
          </div>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
            {casaList.map((c) => (
              <div
                key={c.casa}
                data-testid={`giros-casa-card-${c.casa}`}
                className="rounded-lg border border-white/10 bg-black/40 p-3"
              >
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-sm font-semibold text-white">{c.casa}</span>
                  <span className="text-[0.7rem] uppercase tracking-widest text-zinc-500">{c.count}x</span>
                </div>
                <div
                  className="font-mono-num text-lg font-bold"
                  style={{ color: c.total >= 0 ? "#F472B6" : "#FF3B30" }}
                >
                  {formatBRL(c.total)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h2 className="font-display text-2xl font-bold text-white">Giros</h2>
        <p className="text-xs text-zinc-500">
          Prêmios de giros grátis e promoções — filtrados pelo período, casa e titular acima.
        </p>
      </div>

      {/* Table — apenas Data, Casa e Lucro */}
      {filtered.length === 0 ? (
        <div
          data-testid="giros-empty"
          className="flex flex-col items-center justify-center rounded-xl border border-dashed border-white/10 bg-[#121212] p-16 text-center"
        >
          <Coins className="mb-3 h-10 w-10 text-zinc-700" strokeWidth={1.5} />
          <h3 className="font-display text-lg font-bold text-white">Nenhum giro encontrado</h3>
          <p className="mt-1 max-w-sm text-sm text-zinc-500">
            Ajuste os filtros ou registre novos giros para vê-los aqui.
          </p>
        </div>
      ) : (
        <>
          {/* MOBILE CARDS */}
          <div className="grid grid-cols-1 gap-3 md:hidden">
            {filtered.map((e) => {
              const casa = e.bookmakers?.[0]?.name || "—";
              const isPos = (e.profit || 0) >= 0;
              return (
                <div
                  key={e.id}
                  data-testid={`giros-card-${e.id}`}
                  className="rounded-xl border border-white/10 bg-[#121212] p-4"
                >
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-md border border-[#F472B6]/30 bg-[#F472B6]/10 px-2 py-0.5 text-xs font-semibold text-[#F472B6]">
                      <Coins className="h-3 w-3" />
                      {casa}
                    </span>
                    <span className="font-mono-num text-xs text-zinc-400">{formatDate(e.date)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div
                      className="font-mono-num text-2xl font-bold"
                      style={{ color: isPos ? "#00FF66" : "#FF3B30" }}
                    >
                      {isPos ? "+" : "-"}R$ {Math.abs(e.profit || 0).toFixed(2)}
                    </div>
                    <Button
                      data-testid={`giros-delete-mobile-${e.id}`}
                      variant="ghost"
                      size="icon"
                      onClick={() => setConfirmId(e.id)}
                      className="h-9 w-9 text-zinc-400 hover:bg-red-500/10 hover:text-red-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* DESKTOP TABLE */}
          <div className="hidden overflow-hidden rounded-xl border border-white/10 bg-[#121212] md:block">
          <table className="w-full">
            <thead className="border-b border-white/10 bg-black/40">
              <tr className="text-left">
                <th className="px-5 py-3 text-[0.7rem] font-medium uppercase tracking-widest text-zinc-500">Data</th>
                <th className="px-5 py-3 text-[0.7rem] font-medium uppercase tracking-widest text-zinc-500">Casa</th>
                <th className="px-5 py-3 text-right text-[0.7rem] font-medium uppercase tracking-widest text-zinc-500">Lucro</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => {
                const casa = e.bookmakers?.[0]?.name || "—";
                return (
                  <tr
                    key={e.id}
                    data-testid={`giros-row-${e.id}`}
                    className="border-b border-white/5 hover:bg-white/[0.02]"
                    style={{ transition: "background-color 0.15s ease" }}
                  >
                    <td className="px-5 py-4 font-mono-num text-sm text-zinc-300">{formatDate(e.date)}</td>
                    <td className="px-5 py-4 text-sm text-white">
                      <span className="inline-flex items-center gap-1.5 rounded-md border border-[#F472B6]/30 bg-[#F472B6]/10 px-2 py-0.5 text-xs text-[#F472B6]">
                        <Coins className="h-3 w-3" />
                        {casa}
                      </span>
                    </td>
                    <td
                      className="px-5 py-4 text-right font-mono-num text-base font-bold"
                      style={{ color: (e.profit || 0) >= 0 ? "#00FF66" : "#FF3B30" }}
                    >
                      {(e.profit || 0) >= 0 ? "+" : "-"}R$ {Math.abs(e.profit || 0).toFixed(2)}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <Button
                        data-testid={`giros-delete-${e.id}`}
                        variant="ghost"
                        size="icon"
                        onClick={() => setConfirmId(e.id)}
                        className="h-8 w-8 text-zinc-500 hover:bg-red-500/10 hover:text-red-400"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent className="border-white/10 bg-[#121212]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Excluir registro?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 bg-transparent text-zinc-300 hover:bg-white/5 hover:text-white">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="giros-confirm-delete"
              onClick={() => {
                handleDelete(confirmId);
                setConfirmId(null);
              }}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
